import { VStack, Heading, Image, Text, Box } from '@chakra-ui/react';
import { IBook } from '@/pages/Bookstore/types';
import { PiHeartFill } from 'react-icons/pi';
import { SlEarphones } from 'react-icons/sl';
import { badges } from '@/pages/Bookstore/utils/badges';
import { FaRegStar } from 'react-icons/fa';
import { useOpenBook } from '@/pages/Bookstore/contexts/BookContext';
import { getEditorialIdentifier } from '@/pages/Bookstore/utils/formatters';

interface BookProps {
  book: IBook;
  receivedBook?: boolean;
  deliveryCode?: string;
  bestSellerName?: string;
  showEditorialCode?: boolean;
  monthReference?: number;
  yearReference?: number;
}

const monthNames = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

export function Book({
  book,
  bestSellerName,
  receivedBook,
  deliveryCode,
  showEditorialCode = true,
  monthReference,
  yearReference,
}: BookProps) {
  const { openBook } = useOpenBook();

  const cover = book.coverOptimized || book.cover;
  const magazineMonth = book.referenceMonth ?? book.monthReference ?? book.monthFeatured;
  const magazineYear = book.referenceYear ?? book.yearReference ?? book.monthFeaturedYear;

  const isMagazine = book.type === 'magazine' || book.type === 'magazine-partiu';
  const editionMonth = isMagazine ? (monthReference ?? magazineMonth) : undefined;
  const editionYear = isMagazine ? (yearReference ?? magazineYear) : undefined;
  const editionLabel =
    editionMonth !== undefined && editionYear
      ? `${monthNames[Math.max(0, Math.min(11, editionMonth))]} ${editionYear}`
      : undefined;
  const editorial = getEditorialIdentifier(book, editionMonth, editionYear);

  const onChangeFavorite = (favorite: boolean) => {
    book.isFavorite = favorite;
  };

  return (
    <VStack
      position={'relative'}
      spacing={0}
      cursor={'pointer'}
      onClick={() => openBook(book, receivedBook, onChangeFavorite, deliveryCode, editionMonth, editionYear)}
      align={'flex-start'}
      width={'100%'}
    >
      {book.type === 'audiobook' && (
        <VStack position={'absolute'} top="5px" left="5px">
          <SlEarphones size={25} color={'white'} filter="drop-shadow(0px 0px 3px rgba(0,0,0,1))" />
        </VStack>
      )}
      {book.isBestSeller ||
        (bestSellerName && (
          <Box
            display={'flex'}
            position={'absolute'}
            top={2}
            left={2}
            flexDirection={'row'}
            alignItems={'center'}
            gap={1}
            padding="3px"
            paddingX="10px"
            borderRadius="6px"
            backgroundColor={'#000000B3'}
          >
            <FaRegStar color="white" size={15} />
            <Text fontSize={12} color="white">
              {bestSellerName || 'Best Seller'}
            </Text>
          </Box>
        ))}
      <VStack position={'absolute'} top="5px" right="5px">
        {/* medalhas só nos recebidos; no acervo gratuito não fazem sentido */}
        {receivedBook &&
          badges.map(
            (badge) =>
              book.levels.includes(badge.id) && (
                <Image
                  key={badge.id}
                  filter="drop-shadow(0px 0px 3px rgba(0,0,0,1))"
                  src={`/${badge?.image}`}
                  height={'24px'}
                />
              )
          )}
      </VStack>
      {book.isFavorite && (
        <VStack position={'absolute'} top="5px" right="30px">
          <PiHeartFill fontSize={'20px'} color="red" />
        </VStack>
      )}
      <Image
        boxShadow={'rgba(100, 100, 111, 0.2) 0px 5px 8px 0px'}
        borderRadius={'5px'}
        aspectRatio={0.7}
        border={'1px solid #ededed'}
        width={'100%'}
        objectFit={{ base: 'contain', md: 'cover' }}
        backgroundColor="white"
        src={cover}
      />
      <Heading
        paddingTop="8px"
        width={'100%'}
        whiteSpace={'nowrap'}
        overflowX={'clip'}
        textOverflow={'ellipsis'}
        fontSize={'12px'}
      >
        {book.title}
      </Heading>
      <Text
        color="#787878"
        fontSize={'12px'}
        width={'100%'}
        whiteSpace={'nowrap'}
        overflowX={'clip'}
        textOverflow={'ellipsis'}
      >
        {isMagazine ? editionLabel || '\u00A0' : book.author}
      </Text>
      {showEditorialCode && (
        <Text
          color="#787878"
          fontSize={'12px'}
          width={'100%'}
          whiteSpace={'nowrap'}
          overflowX={'clip'}
          textOverflow={'ellipsis'}
        >
          {editorial ? `${editorial.label}: ${editorial.value}` : '\u00A0'}
        </Text>
      )}
    </VStack>
  );
}
